import { useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
    groessenklasseBestimmen,
    type GroessenklasseAntwort,
} from "../../services/firmendatenService";
import { VERTRAG_FELDER } from "./GesellschaftsvertragDialog";

/**
 * Größenklasse nach § 267 HGB — und was daraus folgt.
 *
 * **Warum das ein Formular ist und kein Abruf.** Weder die Größenklasse noch
 * die Prüfungspflicht stehen im Handelsregister; sie ergeben sich aus drei
 * Zahlen. Der fehleranfällige Teil ist nicht die Beschaffung, sondern die
 * Rechenregel: Eine Gesellschaft ist klein, wenn sie **mindestens zwei der
 * drei** Merkmale nicht überschreitet — nicht alle drei, und nicht eines. Ein
 * Betrieb mit 300 Arbeitnehmern kann klein sein.
 *
 * **Zwei Zahlen genügen oft.** Liegen Umsatz und Arbeitnehmerzahl beide unter
 * der Schwelle, sind schon zwei Merkmale erfüllt — die Bilanzsumme kann daran
 * nichts mehr ändern. Nur wenn eines darüber und eines darunter liegt,
 * entscheidet das dritte, und dann sagt das Ergebnis genau das.
 */
const useStyles = makeStyles({
    kopfzeile: { fontSize: "13px", color: "#6b5a66", marginBottom: "14px", lineHeight: 1.5 },
    felder: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        ...shorthands.gap("10px"),
        marginBottom: "14px",
        "@media (max-width: 720px)": { gridTemplateColumns: "1fr" },
    },
    feld: { display: "flex", flexDirection: "column", ...shorthands.gap("4px"), minWidth: 0 },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66" },
    eingabe: {
        ...shorthands.padding("9px", "10px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "13.5px",
        color: "#241522",
        backgroundColor: "#fff",
        minWidth: 0,
        ":focus": { ...shorthands.borderColor("#7d2169"), outlineStyle: "none" },
    },
    ergebnis: {
        ...shorthands.padding("12px", "14px"),
        ...shorthands.borderRadius("10px"),
        backgroundColor: "#f3f8f5",
        color: "#1f5c3d",
        marginBottom: "12px",
        fontSize: "14px",
        lineHeight: 1.45,
    },
    offen: { backgroundColor: "#fdf6e3", color: "#7a5a18" },
    begruendung: { display: "block", fontSize: "12.5px", marginTop: "5px", lineHeight: 1.45 },
    zeile: {
        display: "flex",
        alignItems: "flex-start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("11px", "2px"),
        ...shorthands.borderTop("1px", "solid", "#ece6ec"),
    },
    inhalt: { flexGrow: 1, minWidth: 0 },
    vorschau: { display: "block", fontSize: "12px", color: "#6b5a66", marginTop: "4px", lineHeight: 1.45 },
    verweis: {
        display: "inline-block",
        marginTop: "6px",
        color: "#7d2169",
        fontWeight: 700,
        textDecorationLine: "underline",
    },
    hinweis: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("8px", "10px"),
        ...shorthands.borderRadius("8px"),
        marginTop: "10px",
        lineHeight: 1.45,
    },
    fehler: { fontSize: "12.5px", color: "#b42318", marginTop: "10px" },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfStark: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": {
            backgroundColor: "#d9c6d4",
            ...shorthands.borderColor("#d9c6d4"),
            color: "#fff",
        },
    },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
});

/** Leere Eingabe heißt „nicht bekannt", nicht „null Euro". */
const zahl = (v: string): number | null => {
    const rein = v.replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", ".");
    if (!rein.trim()) return null;
    const n = Number(rein);
    return Number.isFinite(n) ? n : null;
};

export function GroessenklasseDialog({
    contactId,
    onClose,
    onUebernehmen,
}: {
    contactId: number;
    onClose: () => void;
    onUebernehmen: (werte: Array<{ feld: string; wert: string }>) => void;
}) {
    const s = useStyles();
    const [bilanzsumme, setBilanzsumme] = useState("");
    const [umsatz, setUmsatz] = useState("");
    const [arbeitnehmer, setArbeitnehmer] = useState("");
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [antwort, setAntwort] = useState<GroessenklasseAntwort | null>(null);
    const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());

    const rechnen = async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            const r = await groessenklasseBestimmen(contactId, {
                bilanzsumme: zahl(bilanzsumme),
                umsatz: zahl(umsatz),
                arbeitnehmer: zahl(arbeitnehmer),
            });
            setAntwort(r);
            setGewaehlt(new Set(r.vorschlaege.map((v) => v.feld)));
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    };

    const uebernehmen = () => {
        if (!antwort) return;
        onUebernehmen(
            antwort.vorschlaege
                .filter((v) => gewaehlt.has(v.feld))
                .map((v) => ({ feld: VERTRAG_FELDER[v.feld], wert: v.wert })),
        );
        onClose();
    };

    const nichts = !zahl(bilanzsumme) && !zahl(umsatz) && !zahl(arbeitnehmer);

    return createPortal(
        <div className="kvp-rd-scrim" onClick={onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">§ 267 HGB</div>
                            <h2>Größenklasse und Prüfungspflicht</h2>
                        </div>
                        <button className="dm-x" onClick={onClose} aria-label="Schließen">
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                        </button>
                    </div>
                </div>

                <div className="dm-body">
                    <div className={s.kopfzeile}>
                        Weder die Größenklasse noch die Prüfungspflicht stehen im Handelsregister —
                        sie ergeben sich aus drei Zahlen. <strong>Zwei genügen oft:</strong> Liegen
                        zwei Merkmale unter der Schwelle, ist die Klasse entschieden, das dritte
                        kann daran nichts mehr ändern.
                    </div>

                    <div className={s.felder}>
                        <label className={s.feld}>
                            <span className={s.beschriftung}>Bilanzsumme (€)</span>
                            <input
                                className={s.eingabe}
                                inputMode="numeric"
                                placeholder="unbekannt"
                                value={bilanzsumme}
                                onChange={(e) => setBilanzsumme(e.target.value)}
                            />
                        </label>
                        <label className={s.feld}>
                            <span className={s.beschriftung}>Umsatzerlöse (€)</span>
                            <input
                                className={s.eingabe}
                                inputMode="numeric"
                                placeholder="unbekannt"
                                value={umsatz}
                                onChange={(e) => setUmsatz(e.target.value)}
                            />
                        </label>
                        <label className={s.feld}>
                            <span className={s.beschriftung}>Arbeitnehmer (Ø)</span>
                            <input
                                className={s.eingabe}
                                inputMode="numeric"
                                placeholder="unbekannt"
                                value={arbeitnehmer}
                                onChange={(e) => setArbeitnehmer(e.target.value)}
                            />
                        </label>
                    </div>

                    {antwort && (
                        <div className={mergeClasses(s.ergebnis, antwort.klasseText ? undefined : s.offen)}>
                            {antwort.klasseText ?? "Die Klasse ließ sich nicht bestimmen."}
                            {antwort.befund.begruendung.map((b, i) => (
                                <span key={i} className={s.begruendung}>{b}</span>
                            ))}
                        </div>
                    )}

                    {antwort?.vorschlaege.map((v) => (
                        <div key={v.feld} className={s.zeile}>
                            <input
                                type="checkbox"
                                checked={gewaehlt.has(v.feld)}
                                onChange={() =>
                                    setGewaehlt((alt) => {
                                        const neu = new Set(alt);
                                        if (neu.has(v.feld)) neu.delete(v.feld);
                                        else neu.add(v.feld);
                                        return neu;
                                    })
                                }
                                aria-label={v.bezeichnung}
                                style={{ marginTop: 3, accentColor: "#7d2169" }}
                            />
                            <span className={s.inhalt}>
                                <span className={s.beschriftung}>{v.bezeichnung}</span>{" "}
                                <strong>{v.wert}</strong>
                                {v.vorhanden && (
                                    <span className={s.vorschau}>
                                        Im Feld steht: „{v.vorhanden}" — wird ersetzt
                                    </span>
                                )}
                                {v.begruendung && <span className={s.vorschau}>{v.begruendung}</span>}
                            </span>
                        </div>
                    ))}

                    {(antwort?.hinweise ?? []).map((h, i) => (
                        <div key={i} className={s.hinweis}>
                            {h}
                            {/* Nur beim Bestätigungsvermerk: der Weg dorthin. Ein
                                vorbelegter Link ist unmöglich — der Bundesanzeiger
                                verhindert Direktaufrufe absichtlich („Sie bewegen
                                sich in einer Session"). Deshalb der Einstieg plus
                                der Firmenname zum Einfügen. */}
                            {h.includes("Bestätigungsvermerk") && h.includes("Bundesanzeiger") && (
                                <a
                                    className={s.verweis}
                                    href="https://www.bundesanzeiger.de/pub/de/suche"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Bundesanzeiger-Suche öffnen ↗
                                </a>
                            )}
                        </div>
                    ))}
                    {fehler && <div className={s.fehler}>{fehler}</div>}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    <button type="button" className={s.knopf} onClick={onClose}>
                        {antwort?.vorschlaege.length ? "Abbrechen" : "Schließen"}
                    </button>
                    <button
                        type="button"
                        className={s.knopf}
                        disabled={laeuft || nichts}
                        onClick={() => void rechnen()}
                    >
                        {laeuft ? "wird bestimmt …" : "Bestimmen"}
                    </button>
                    {!!antwort?.vorschlaege.length && (
                        <button
                            type="button"
                            className={mergeClasses(s.knopf, s.knopfStark)}
                            disabled={gewaehlt.size === 0}
                            onClick={uebernehmen}
                        >
                            {gewaehlt.size} übernehmen
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
